import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBookingDraft } from '../../contexts/BookingDraftContext';
import { useAuth } from '../../contexts/AuthContext';
import { photographerService } from '../../services/photographerService';
import { supabase } from '../../lib/supabase';
import { buildPreciseLocationFromSelection } from '../../utils/preciseLocationBuilder';
import { isPhotographyTypeAllowedForCategory } from '../../utils/photographyTypesVisibility';
import { resolveExperienceLevel } from '../../utils/experienceLevel';
import Header from '../../components/layout/Header';
import StepIndicator from './components/StepIndicator';
import PhotographyCategoryStep from './components/PhotographyCategoryStep';
import RegionSelectionStep from './components/RegionSelectionStep';
import CitySelectionStep from './components/CitySelectionStep';
import ShootingPlaceTypeStep from './components/ShootingPlaceTypeStep';
import PreciseLocationStep from './components/PreciseLocationStep';
import ShootingMethodStep from './components/ShootingMethodStep';
import PhotographyTypeStep from './components/PhotographyTypeStep';
import WorkloadBucketStep from './components/WorkloadBucketStep';
import DeliverableCountStep from './components/DeliverableCountStep';
import DurationStep from './components/DurationStep';
import DateTimeStep from './components/DateTimeStep';
import EquipmentStep from './components/EquipmentStep';
import ExperienceLevelStep from './components/ExperienceLevelStep';
import PackagesStep from './components/PackagesStep';
import PricingPanel from './components/PricingPanel';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import BackButton from '../../components/BackButton';

// Default draft object defined outside component to avoid re-creation on each render
const DEFAULT_DRAFT_OBJECT = {
  photographyCategory: '', // commercial | individual — الخطوة الأولى (إجبارية)
  photographyCategoryLabel: '',
  region: '', // SAFE MODE: Region for discovery (eastern/central/western/northern/southern)
  city: '', // Auto-derived from precise location
  district: '', // District/neighborhood (حي) - auto-derived
  shootingPlaceType: '',
  preciseLocation: null,
  shootingMethod: '', // أول وسيلة مختارة (للتسعير والتوافق مع الـ API)
  shootingMethods: [], // وسائل التصوير المختارة (واحدة أو أكثر)
  shootingMethodLabels: [],
  photographyType: '',
  workloadBucket: '', // Workload bucket (one_item, small, medium, large, xlarge للمنتجات)
  productPhysicalSizeBucket: '', // تصوير منتجات: compact | standard | bulky
  productPhysicalSizeLabel: '',
  // Deliverables (photos/videos count)
  deliverableMode: 'custom', // 'custom' or 'unspecified'
  photosCount: 10, // Default 10 photos
  videosCount: 0, // Default 0 long videos
  shortVideosCount: 0, // Default 0 short videos (social media)
  duration: '',
  date: '',
  time: '',
  bookingDate: '', // For pricing calculation
  equipment: [],
  experienceLevel: '',
  // Labels
  regionLabel: '',
  cityLabel: '',
  shootingPlaceTypeLabel: '',
  shootingMethodLabel: '',
  photographyTypeLabel: '',
  deliverableLabel: '10 صورة',
  durationLabel: '',
  experienceLevelLabel: ''
};

const BookingWizard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { draft, updateDraft } = useBookingDraft();
  const { userProfile, loading: authLoading } = useAuth();
  const returnTo = searchParams.get('return') || null;
  const stepParam = searchParams.get('step');
  const mode = searchParams.get('mode');
  const photographerIdParam = searchParams.get('photographerId');
  
  // 🔒 ROLE PROTECTION: Prevent photographers from making bookings
  useEffect(() => {
    if (!authLoading && userProfile?.role === 'photographer') {
      console.log('🚫 Photographer attempted to access booking wizard - redirecting to dashboard');
      navigate('/photographer-dashboard', { replace: true });
    }
  }, [userProfile, authLoading, navigate]);
  
  const [currentStep, setCurrentStep] = useState(stepParam ? parseInt(stepParam) : 1);
  const stepContentRef = useRef(null);
  const stepQuestionRef = useRef(null); // scroll target: top of question (title) so user sees the question first
  const didInitRef = useRef(false);
  const [formData, setFormData] = useState(DEFAULT_DRAFT_OBJECT);
  const formDataRef = useRef(formData); // SAFE MODE: Store formData for immediate access
  const [validationKey, setValidationKey] = useState(0); // SAFE MODE: Force re-render after formDataRef update
  const [isDirectMode, setIsDirectMode] = useState(mode === 'direct');
  const [preselectedPhotographer, setPreselectedPhotographer] = useState(null);
  const [loadingPhotographer, setLoadingPhotographer] = useState(false);
  const [pendingBookingCheck, setPendingBookingCheck] = useState({ loading: false, hasPending: false, booking: null });

  // SAFE MODE: Block new booking if customer has any active booking.
  // Allowed to book again only when existing booking is: completed / cancelled / declined / expired.
  const ACTIVE_BOOKING_STATUSES = [
    'pending_photographer_confirmation',
    'pending_photographer_assignment',
    'pending',
    'awaiting_payment',
    'paid_confirmed',
    'confirmed',
    'session_started',
    'awaiting_delivery',
  ];

  const ACTIVE_STATUS_LABEL = {
    pending_photographer_confirmation: 'في انتظار تأكيد المصور',
    pending_photographer_assignment:   'جاري تعيين مصور',
    pending:                           'قيد الانتظار',
    awaiting_payment:                  'في انتظار الدفع',
    paid_confirmed:                    'مؤكد',
    confirmed:                         'مؤكد',
    session_started:                   'الجلسة جارية الآن',
    awaiting_delivery:                 'في انتظار التسليم',
  };

  useEffect(() => {
    const checkActiveBooking = async () => {
      if (returnTo || isDirectMode || photographerIdParam) return;
      try {
        setPendingBookingCheck({ loading: true, hasPending: false, booking: null });
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) { setPendingBookingCheck({ loading: false, hasPending: false, booking: null }); return; }

        const { data: bookings, error } = await supabase
          .from('bookings')
          .select('id, status, created_at, booking_date, photography_type')
          .eq('customer_id', user.id)
          .in('status', ACTIVE_BOOKING_STATUSES)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) { console.warn('Failed to check active bookings:', error); setPendingBookingCheck({ loading: false, hasPending: false, booking: null }); return; }

        if (bookings?.length > 0) {
          setPendingBookingCheck({ loading: false, hasPending: true, booking: bookings[0] });
        } else {
          setPendingBookingCheck({ loading: false, hasPending: false, booking: null });
        }
      } catch (err) {
        console.error('Error checking active bookings:', err);
        setPendingBookingCheck({ loading: false, hasPending: false, booking: null });
      }
    };
    checkActiveBooking();
  }, [returnTo, isDirectMode, photographerIdParam]);

  // Load photographer data for direct mode
  useEffect(() => {
    if (isDirectMode && photographerIdParam && !preselectedPhotographer) {
      setLoadingPhotographer(true);
      photographerService.getPhotographer(photographerIdParam)
        .then(photographer => {
          setPreselectedPhotographer(photographer);
          
          // Get equipment data
          const equipmentData = photographer.equipmentAvailable || photographer.equipment_available || [];
          const equipmentArray = Array.isArray(equipmentData) ? equipmentData : [];
          
          console.log('📸 Direct Booking - Photographer loaded:', {
            photographerId: photographerIdParam,
            name: photographer.user?.fullName || photographer.user?.full_name || photographer.name,
            equipmentAvailable: equipmentData,
            equipmentArray: equipmentArray,
            equipmentLength: equipmentArray.length,
            hasStudio: photographer.hasStudio || photographer.has_studio,
            studioLocation: photographer.studioLocation || photographer.studio_location
          });
          
          // Set booking mode and preselected photographer ID in draft
          // Also store photographer profile data for use in steps
          // SAFE MODE: Include service center coordinates and radius for distance validation
          const photographerExp = resolveExperienceLevel(
            photographer.experienceLevel || photographer.experience_level,
            'distinguished'
          );

          updateDraft({
            bookingMode: 'direct',
            preselectedPhotographerId: photographerIdParam,
            photographerId: photographerIdParam,
            experienceLevel: photographerExp.ui,
            experienceLevelLabel: photographerExp.labelAr,
            directPhotographerProfile: {
              id: photographerIdParam,
              name: photographer.user?.fullName || photographer.user?.full_name || photographer.name || 'مصور',
              city: photographer.city,
              experienceLevel: photographerExp.ui,
              specializations: photographer.specializations || [],
              equipmentAvailable: equipmentArray,
              shootingMethods: photographer.shootingMethods || photographer.shooting_methods || [],
              avatar: photographer.user?.avatar_url || photographer.user?.avatarUrl,
              hasStudio: photographer.hasStudio || photographer.has_studio || false,
              studioLocation: photographer.studioLocation || photographer.studio_location || null,
              // Service radius data for distance validation
              serviceCenterLat: photographer.serviceCenterLat || photographer.service_center_lat || null,
              serviceCenterLng: photographer.serviceCenterLng || photographer.service_center_lng || null,
              serviceRadiusKm: photographer.serviceRadiusKm || photographer.service_radius_km || 20,
              // التوفر الأسبوعي لتصفية الأوقات في الحجز المباشر
              weeklyAvailability: photographer.weeklyAvailability ?? photographer.weekly_availability ?? null
            }
          });
          // SAFE MODE: Do not overwrite user's location in draft (and thus in header) when
          // they already set it via the header button or GPS. Use photographer city only
          // inside directPhotographerProfile for booking context.
          const isUserLocationFromHeaderOrGps =
            draft?.locationSource === 'global-header' ||
            draft?.locationSource === 'user-location';
          if (photographer.city && !isUserLocationFromHeaderOrGps) {
            updateDraft({
              city: photographer.city,
              cityLabel: photographer.city
            });
          }
        })
        .catch(error => {
          console.error('Error loading photographer for direct booking:', error);
        })
        .finally(() => {
          setLoadingPhotographer(false);
        });
    }
  }, [isDirectMode, photographerIdParam, preselectedPhotographer, updateDraft, draft]);

  // Initialize draft only once on mount (guarded to prevent render-time updates)
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    
    // Check if this is a new booking (no mode param and no preselected photographer)
    const isNewBooking = !mode && !photographerIdParam;
    
    // Only initialize if no draft exists
    if (!draft) {
      const initialDraft = { ...DEFAULT_DRAFT_OBJECT };
      // Prefill precise location from stored user_location (onboarding/global header) if available
      try {
        if (typeof localStorage !== 'undefined') {
          const rawLocation = localStorage.getItem('user_location');
          if (rawLocation) {
            const parsed = JSON.parse(rawLocation);
            const hasCoords =
              parsed?.lat != null &&
              parsed?.lng != null &&
              Number.isFinite(Number(parsed.lat)) &&
              Number.isFinite(Number(parsed.lng));

            if (hasCoords) {
              let meta = null;
              try {
                const rawMeta = localStorage.getItem('user_location_meta');
                if (rawMeta) {
                  meta = JSON.parse(rawMeta);
                }
              } catch {
                meta = null;
              }

              const selection = {
                lat: parsed.lat,
                lng: parsed.lng,
                city: meta?.city,
                district: meta?.district,
                address: meta?.address_text,
              };

              const locationUpdate = buildPreciseLocationFromSelection(
                selection,
                initialDraft
              );

              if (locationUpdate && locationUpdate.preciseLocation) {
                Object.assign(initialDraft, locationUpdate, {
                  locationSource: 'user-location',
                  // null = فحص النطاق معلّق حتى تعمل PreciseLocationStep
                  isOutsideServiceRange: null,
                });
              }
            }
          }
        }
      } catch {
        // Ignore parsing/storage errors
      }

      if (isDirectMode) {
        initialDraft.bookingMode = 'direct';
        initialDraft.preselectedPhotographerId = photographerIdParam || null;
      }
      updateDraft(initialDraft);
    } else {
      // If draft exists, check if we need to clear location data
      const isExternalLocationSource =
        draft.locationSource === 'global-header' ||
        draft.locationSource === 'user-location';
      const hasOldLocation = draft.preciseLocation !== null && draft.preciseLocation !== undefined;
      const currentCity = draft.city;
      
      // Clear location if:
      // 1. Starting a new booking (not direct mode, no preselected photographer)
      // 2. Direct mode but photographer changed
      // 3. City changed (will be handled by handleUpdateFormData, but also check here)
      const shouldClearLocation = hasOldLocation && !isExternalLocationSource && (
        isNewBooking || // New booking from browse-photographers
        (isDirectMode && photographerIdParam && draft.preselectedPhotographerId !== photographerIdParam) || // Different photographer in direct mode
        (!currentCity) // No city set (fresh start)
      );
      
      if (shouldClearLocation) {
        console.log('🧹 Clearing old location data for new booking:', {
          isNewBooking,
          isDirectMode,
          photographerIdParam,
          oldPhotographerId: draft.preselectedPhotographerId,
          hasOldLocation,
          currentCity
        });
        updateDraft({
          preciseLocation: null,
          district: '',
          // Keep city if it exists and matches, otherwise let user select
        });
      }
    }
  }, [updateDraft, draft, isDirectMode, photographerIdParam, mode]);

  // SAFE MODE: Keep formDataRef in sync with formData
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Load draft data on mount and when draft changes
  useEffect(() => {
    if (draft) {
      setFormData(prev => {
        const updated = { ...prev, ...draft };
        // SAFE MODE: Update ref immediately for synchronous access
        formDataRef.current = updated;
        return updated;
      });
    }
  }, [draft]);

  // SAFE MODE: Merge draft and formDataRef for immediate validation
  // This ensures isStepValid uses the latest data even before formData state updates
  const mergedFormData = useMemo(() => {
    // Use formDataRef.current for immediate access to latest data
    const currentFormData = formDataRef.current || formData;
    return draft ? { ...currentFormData, ...draft } : currentFormData;
  }, [draft, formData]);

  // مسودات قديمة أو ?step= بدون فئة تصوير — إرجاع للخطوة 1
  useEffect(() => {
    const cat = mergedFormData?.photographyCategory;
    const hasCat = cat === 'commercial' || cat === 'individual';
    if (currentStep > 1 && !hasCat) {
      setCurrentStep(1);
    }
  }, [currentStep, mergedFormData?.photographyCategory]);

  // Scroll to top of page when step changes so the question title is at the top of the screen
  useEffect(() => {
    const t1 = setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 0);
    const t2 = setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 320);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [currentStep, formData.photographyType, formData.photographyCategory]);

  // SAFE MODE: فئة التصوير أولاً، ثم باقي الخطوات
  // Direct: تُخفى المنطقة/المدينة (2) ومستوى الخبرة (12)
  const steps = [
    { id: 1, label: 'فئة التصوير', component: PhotographyCategoryStep },
    { id: 2, label: 'المنطقة', component: isDirectMode ? CitySelectionStep : RegionSelectionStep },
    { id: 3, label: 'الموقع الدقيق', component: PreciseLocationStep },
    { id: 4, label: 'نوع التصوير', component: PhotographyTypeStep },
    { id: 5, label: 'مكان التصوير', component: ShootingPlaceTypeStep },
    { id: 6, label: 'وسيلة التصوير', component: ShootingMethodStep },
    { id: 7, label: 'حجم العمل', component: WorkloadBucketStep, conditional: true },
    { id: 8, label: 'الباقة', component: PackagesStep },
    { id: 9, label: 'المدة', component: DurationStep },
    { id: 10, label: 'التاريخ والوقت', component: DateTimeStep },
    { id: 11, label: 'التعديل', component: EquipmentStep },
    { id: 12, label: 'مستوى الخبرة', component: ExperienceLevelStep }
  ];

  // Check if step should be shown (for conditional steps)
  const shouldShowStep = (stepId) => {
    const step = steps[stepId - 1];
    
    // إخفاء خطوة المنطقة/المدينة (2) في الحجز المباشر
    if (stepId === 2 && isDirectMode) {
      return false;
    }
    
    // إخفاء مستوى الخبرة (12) في الحجز المباشر
    if (stepId === 12 && isDirectMode) {
      return false;
    }

    // خطوة المدة (9) ومستوى الخبرة (12) مدمجتان في خطوة الباقة (8) في كلا النوعين
    if (stepId === 9 || stepId === 12) {
      return false;
    }
    
    if (!step?.conditional) return true;
    
    // حجم العمل (7) — طعام، منتجات، مناسبات، عقارات، زفاف/شخصي (أفراد)
    if (stepId === 7) {
      const currentFormData = formDataRef.current || formData;
      const currentData = draft ? { ...currentFormData, ...draft } : currentFormData;
      const pt = currentData?.photographyType;
      const cat = currentData?.photographyCategory;
      if (cat === 'individual' && (pt === 'wedding' || pt === 'portrait')) return true;
      return pt === 'food' || pt === 'product' || pt === 'event' || pt === 'real-estate';
    }
    
    return true;
  };

  // Get visible steps (filter out conditional steps that shouldn't be shown)
  const getVisibleSteps = () => {
    return steps.filter((step, index) => {
      const stepId = index + 1;
      return shouldShowStep(stepId);
    });
  };

  // Get current visible step index
  const getCurrentVisibleStepIndex = () => {
    let visibleIndex = 0;
    for (let i = 0; i < currentStep; i++) {
      if (shouldShowStep(i + 1)) {
        visibleIndex++;
      }
    }
    return visibleIndex;
  };

  // Get next step number (skipping conditional steps)
  const getNextStep = (fromStep) => {
    for (let i = fromStep; i < steps.length; i++) {
      if (shouldShowStep(i + 1)) {
        return i + 1;
      }
    }
    return steps.length + 1; // End of wizard
  };

  // Get previous step number (skipping conditional steps)
  const getPreviousStep = (fromStep) => {
    for (let i = fromStep - 2; i >= 0; i--) {
      if (shouldShowStep(i + 1)) {
        return i + 1;
      }
    }
    return 1;
  };

  const handleUpdateFormData = (data) => {
    // SAFE MODE: Calculate updated data BEFORE setFormData to ensure synchronous update
    const currentFormData = formDataRef.current || formData;
    const updated = { ...currentFormData, ...data };
    
    // SAFE MODE: Only clear location if city changed AND we're NOT setting a new location
    // This prevents clearing location when saving from map (which sends city + preciseLocation together)
    if (data.city && data.city !== currentFormData.city && !data.preciseLocation) {
      console.log('🏙️ City changed (no new location), clearing location data:', {
        oldCity: currentFormData.city,
        newCity: data.city
      });
      updated.preciseLocation = null;
      updated.district = '';
    }
    
    // Update bookingDate when date changes
    if (data.date) {
      updated.bookingDate = data.date;
    }

    // عند تغيير نوع التصوير: إزالة حقول خاصة بتصوير المنتجات
    if (data.photographyType !== undefined && data.photographyType !== currentFormData.photographyType) {
      if (data.photographyType !== 'product') {
        updated.productPhysicalSizeBucket = '';
        updated.productPhysicalSizeLabel = '';
        // مسح خيار الاستديو إذا كان محدداً وتغيّر النوع لغير المنتجات
        if (currentFormData.shootingPlaceType === 'photographer_studio') {
          updated.shootingPlaceType = '';
          updated.shootingPlaceTypeLabel = '';
        }
      }
    }
    
    // SAFE MODE: Log location updates to track saving
    if (data.preciseLocation) {
      console.log('📍 handleUpdateFormData: Updating preciseLocation:', {
        lat: data.preciseLocation.lat,
        lng: data.preciseLocation.lng,
        address_text: data.preciseLocation.address_text,
        city: data.preciseLocation.city,
        fullPreciseLocation: data.preciseLocation
      });
    }
    
    // SAFE MODE: Update ref IMMEDIATELY and SYNCHRONOUSLY before setFormData
    // This ensures isStepValid() can access the latest data immediately when user clicks "التالي"
    formDataRef.current = updated;
    console.log('📍 handleUpdateFormData: Updated formDataRef.current IMMEDIATELY:', {
      hasPreciseLocation: !!formDataRef.current?.preciseLocation,
      lat: formDataRef.current?.preciseLocation?.lat,
      lng: formDataRef.current?.preciseLocation?.lng
    });
    
    // Now update state (async, but ref is already updated)
    setFormData(prev => {
      // Merge with previous state to ensure we don't lose any data
      const merged = { ...prev, ...updated };
      return merged;
    });
    
    // Save to draft context
    // SAFE MODE: In direct booking, do not write session location (city/cityLabel/district/locationSource)
    // into draft so that the header keeps showing the user's location, not the session location.
    let draftPayload = updated;
    if (isDirectMode) {
      draftPayload = { ...updated };
      delete draftPayload.city;
      delete draftPayload.cityLabel;
      delete draftPayload.district;
      delete draftPayload.locationSource;
    }
    updateDraft(draftPayload);
    
    // SAFE MODE: Force re-render immediately to ensure button state updates
    if (data.preciseLocation) {
      setValidationKey(prev => prev + 1);
      console.log('📍 handleUpdateFormData: Triggered re-render for validation');
    }
  };

  const handleNext = () => {
    // Check if we're at the last visible step
    const nextStep = getNextStep(currentStep);
    
    if (nextStep <= steps.length) {
      setCurrentStep(nextStep);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 0);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 320);
    } else {
      // Save final form data to draft before navigating
      const finalData = {
        ...formData,
        bookingMode: isDirectMode ? 'direct' : undefined,
        preselectedPhotographerId: isDirectMode ? photographerIdParam : undefined,
        photographerId: isDirectMode ? photographerIdParam : undefined
      };
      updateDraft(finalData);
      
      // In direct mode, navigate to direct checkout
      if (isDirectMode) {
        navigate('/checkout/direct');
      } else if (returnTo === 'checkout') {
        navigate('/checkout');
      } else {
        // Navigate to photographer results with booking data
        navigate('/photographer-results', { state: { bookingData: finalData } });
      }
    }
  };
  
  // Handle step save when returning from checkout
  const handleStepSave = () => {
    updateDraft(formData);
    if (returnTo === 'checkout') {
      navigate('/checkout');
    }
  };

  const handlePrevious = () => {
    const prevStep = getPreviousStep(currentStep);
    if (prevStep >= 1) {
      setCurrentStep(prevStep);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 0);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 320);
    }
  };

  const isStepValid = () => {
    // SAFE MODE: Use formDataRef.current and draft for immediate validation
    // formDataRef.current is updated synchronously, ensuring latest data is used
    const currentFormData = formDataRef.current || formData;
    const currentData = draft ? { ...currentFormData, ...draft } : currentFormData;
    
    switch (currentStep) {
      case 1:
        // فئة التصوير (إجبارية)
        return currentData?.photographyCategory === 'commercial' || currentData?.photographyCategory === 'individual';
      case 2:
        // المنطقة — في المباشر تُخفى الخطوة فيُعتبر صالحاً عند عدم عرضها
        if (isDirectMode) return true;
        return currentData?.region !== '';
      case 3:
        // الموقع الدقيق
        // SAFE MODE: Check for valid coordinates (address_text may be empty but coordinates are required)
        const preciseLocation = currentData?.preciseLocation;
        const hasValidLocation = preciseLocation?.lat && 
                                  preciseLocation?.lng && 
                                  !isNaN(preciseLocation.lat) && 
                                  !isNaN(preciseLocation.lng);
        
        // SAFE MODE: In direct mode, also check service range validation
        // Allow if: within range OR exception requested
        if (isDirectMode && hasValidLocation) {
          const rangeStatus = currentData?.isOutsideServiceRange; // null=pending | false=ok | true=outside | undefined=old-draft
          const exceptionRequested = currentData?.exceptionRequested === true;

          // null = فحص النطاق لم يكتمل بعد (موقع مُعبَّأ مسبقاً) — أوقف المتابعة
          if (rangeStatus === null) {
            console.log('⏳ Step 3 blocked: range check still pending for pre-filled location');
            return false;
          }

          // true = خارج النطاق بدون استثناء — أوقف المتابعة
          if (rangeStatus === true && !exceptionRequested) {
            console.log('❌ Step 3 validation failed (outside service range, no exception):', {
              rangeStatus,
              exceptionRequested,
              distanceFromPhotographerKm: currentData?.distanceFromPhotographerKm
            });
            return false;
          }
          // false = داخل النطاق ✅ | undefined = draft قديم (سلوك متوافق للخلف) ✅
        }
        
        // Debug log to track validation
        if (!hasValidLocation) {
          console.log('❌ Step 3 (Precise Location) validation failed:', {
            hasPreciseLocation: !!preciseLocation,
            lat: preciseLocation?.lat,
            lng: preciseLocation?.lng,
            address_text: preciseLocation?.address_text
          });
        } else {
          console.log('✅ Step 3 (Precise Location) validation passed:', {
            lat: preciseLocation.lat,
            lng: preciseLocation.lng,
            isDirectMode,
            isOutsideServiceRange: currentData?.isOutsideServiceRange,
            exceptionRequested: currentData?.exceptionRequested
          });
        }
        
        return hasValidLocation;
      case 4: {
        // نوع التصوير — يجب أن يطابق فئة التصوير (تجاري / أفراد)
        const pt = currentData?.photographyType;
        if (!pt) return false;
        return isPhotographyTypeAllowedForCategory(pt, currentData?.photographyCategory);
      }
      case 5:
        // مكان التصوير
        return currentData?.shootingPlaceType !== '';
      case 6:
        // وسيلة التصوير (واحدة أو أكثر)
        return (Array.isArray(currentData?.shootingMethods) && currentData.shootingMethods.length > 0) || currentData?.shootingMethod !== '';
      case 7:
        // حجم العمل (conditional)
        if (!shouldShowStep(7)) return true;
        if (currentData?.photographyType === 'product') {
          return (
            currentData?.workloadBucket !== '' && currentData?.productPhysicalSizeBucket !== ''
          );
        }
        return currentData?.workloadBucket !== '';
      case 8:
        // الباقة — مدمجة في كلا النوعين
        if (isDirectMode) {
          // في الحجز المباشر: المدة + مخرجات (مستوى الخبرة مقفّل من المصور)
          return (
            currentData?.duration !== '' &&
            (currentData?.photosCount > 0 || currentData?.videosCount > 0 || currentData?.shortVideosCount > 0)
          );
        }
        // في الحجز العادي: مستوى الخبرة + المدة + مخرجات
        return (
          currentData?.experienceLevel !== '' &&
          currentData?.duration !== '' &&
          (currentData?.photosCount > 0 || currentData?.videosCount > 0 || currentData?.shortVideosCount > 0)
        );
      case 9:
        // مدمجة في خطوة الباقة دائماً
        return true;
      case 10:
        // التاريخ والوقت — التحقق من عدم كون التاريخ في الماضي
        const dateStr = currentData?.date;
        const timeStr = currentData?.time;
        if (!dateStr || !timeStr) return false;
        const todayStr = new Date().toISOString().split('T')[0];
        if (dateStr < todayStr) return false; // التاريخ في الماضي
        if (dateStr === todayStr) {
          const [h, m] = timeStr.split(':').map(Number);
          const slotTime = new Date();
          slotTime.setHours(h, m || 0, 0, 0);
          const minAllowed = new Date(Date.now() + 2 * 60 * 60 * 1000);
          if (slotTime < minAllowed) return false; // اليوم: يجب أن يكون الوقت بعد ساعتين
        }
        return true;
      case 11:
        // المعدات (اختياري)
        return true;
      case 12:
        // مستوى الخبرة — في الحجز المباشر مخفية، وفي العادي مدمجة في خطوة الباقة
        return true;
      default:
        return false;
    }
  };

  const CurrentStepComponent = steps?.[currentStep - 1]?.component;
  const isLastStep = getNextStep(currentStep) > steps.length;

  // SAFE MODE: Show pending booking message if user has active pending booking
  if (pendingBookingCheck.hasPending && !returnTo && !isDirectMode) {
    const activeBooking = pendingBookingCheck.booking;
    const statusLabel = ACTIVE_STATUS_LABEL[activeBooking?.status] || 'نشط';
    const bookingDate = activeBooking?.booking_date
      ? new Date(activeBooking.booking_date).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
      : null;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center px-4 py-12" dir="rtl">
        <div className="w-full max-w-lg">
          {/* Card */}
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
            {/* Top accent bar */}
            <div className="h-1.5 bg-gradient-to-r from-amber-400 to-orange-400" />

            <div className="p-8 text-center">
              {/* Icon */}
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Icon name="CalendarClock" size={30} className="text-amber-500" />
              </div>

              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                لديك حجز نشط
              </h1>
              <p className="text-slate-500 text-sm leading-relaxed mb-6">
                يمكنك إنشاء حجز جديد فقط بعد اكتمال حجزك الحالي أو إلغائه.
              </p>

              {/* Booking status chip */}
              <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                {statusLabel}
              </div>

              {/* Booking details */}
              {bookingDate && (
                <div className="flex items-center justify-center gap-2 text-slate-500 text-sm mb-6">
                  <Icon name="Calendar" size={15} />
                  <span>موعد الجلسة: {bookingDate}</span>
                </div>
              )}

              {/* Divider */}
              <div className="border-t border-slate-100 my-6" />

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate('/customer-dashboard/bookings')}
                  className="flex-1 flex items-center justify-center gap-2 bg-primary hover:opacity-90 text-white font-bold py-3 px-5 rounded-2xl transition shadow-md shadow-primary/20"
                >
                  <Icon name="Calendar" size={17} />
                  إدارة حجزي الحالي
                </button>
                <button
                  onClick={() => navigate('/customer-dashboard')}
                  className="flex-1 flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold py-3 px-5 rounded-2xl transition"
                >
                  <Icon name="ArrowRight" size={17} />
                  لوحة التحكم
                </button>
              </div>
            </div>
          </div>

          {/* Helper note */}
          <p className="text-center text-xs text-slate-400 mt-4 leading-relaxed">
            بعد اكتمال الجلسة أو إلغاء الحجز ستتمكن من إنشاء حجز جديد
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50">
      <Header />
      {/* Mobile-optimized layout: full viewport for question content */}
      <div className="pt-16 md:pt-20 pb-4 md:pb-20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
          <div className="mb-4">
            <BackButton to={-1} label="رجوع" />
          </div>
          {/* Page Header - Hidden on mobile to save space */}
          <div className="mb-4 md:mb-8 text-center hidden md:block">
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-700 to-blue-600 bg-clip-text text-transparent mb-3">
              احجز جلسة تصوير احترافية
            </h1>
            <p className="text-lg text-slate-600">
              أكمل الخطوات التالية لحجز جلسة التصوير المناسبة لك
            </p>
          </div>

          {/* Step Indicator - Compact on mobile */}
          <div className="mb-4 md:mb-6">
            <StepIndicator 
              currentStep={getCurrentVisibleStepIndex() + 1} 
              totalSteps={getVisibleSteps()?.length} 
              steps={getVisibleSteps()} 
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8">
            {/* Question Content - Full viewport on mobile */}
            <div className="lg:col-span-2 order-2 lg:order-1 flex flex-col min-h-[calc(100vh-10rem)] md:min-h-0" ref={stepContentRef}>
              <div className="flex flex-col pt-6 md:pt-4">
                {shouldShowStep(currentStep) && (
                  <div ref={stepQuestionRef}>
                    <CurrentStepComponent 
                      formData={formData} 
                      onUpdate={handleUpdateFormData}
                      isDirectMode={isDirectMode}
                      preselectedPhotographer={preselectedPhotographer}
                    />
                  </div>
                )}
              </div>

              {/* Navigation Buttons - Fixed at bottom on mobile */}
              <div className="flex items-center justify-between gap-3 md:gap-4 mt-4 md:mt-8 pt-4 border-t border-slate-200 md:border-none sticky bottom-0 bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 md:bg-transparent -mx-4 px-4 md:mx-0 md:px-0 pb-2 md:pb-0">
                {returnTo === 'checkout' && currentStep > 1 && (
                  <Button
                    variant="outline"
                    onClick={() => navigate('/checkout')}
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="flex-1 md:flex-initial border-slate-300"
                  >
                    العودة إلى التأكيد
                  </Button>
                )}
                {(!returnTo || returnTo !== 'checkout' || currentStep === 1) && (
                  <Button
                    variant="outline"
                    onClick={handlePrevious}
                    disabled={currentStep === 1}
                    iconName="ChevronRight"
                    iconPosition="right"
                    className="flex-1 md:flex-initial border-slate-300"
                  >
                    السابق
                  </Button>
                )}

                {returnTo === 'checkout' && !isLastStep ? (
                  <Button
                    variant="default"
                    onClick={() => {
                      handleStepSave();
                      const nextStep = getNextStep(currentStep);
                      if (nextStep <= steps.length) {
                        setCurrentStep(nextStep);
                        setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 0);
                        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 320);
                      }
                    }}
                    disabled={!isStepValid()}
                    iconName="Check"
                    iconPosition="right"
                    className="flex-1 md:flex-initial bg-button hover:opacity-90 text-button-foreground"
                  >
                    حفظ والمتابعة
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    onClick={handleNext}
                    disabled={!isStepValid()}
                    iconName={isLastStep ? 'Search' : 'ChevronLeft'}
                    iconPosition="right"
                    className="flex-1 md:flex-initial bg-button hover:opacity-90 text-button-foreground"
                  >
                    {isLastStep ? (isDirectMode ? 'متابعة لإتمام الحجز' : 'ابحث عن مصور') : 'التالي'}
                  </Button>
                )}
              </div>
            </div>

            {/* Pricing Panel - Hidden on all screens (summary shown in last step instead) */}
            <div className="lg:col-span-1 order-1 lg:order-2 hidden">
              <div className="sticky top-24">
                <PricingPanel formData={formData} />
              </div>
            </div>
          </div>

          {/* Trust Section - Hidden on mobile to save space */}
          <div className="mt-8 md:mt-12 bg-white rounded-xl p-4 md:p-6 shadow-sm border border-slate-100 hidden md:block">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Shield" size={24} className="text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  ضمان الجودة والأمان
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  جميع المصورين على منصتنا محترفون ومعتمدون. نضمن لك تجربة تصوير احترافية وآمنة.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircle2" size={16} className="text-success" />
                    <span className="text-sm text-slate-700">مصورون معتمدون</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircle2" size={16} className="text-success" />
                    <span className="text-sm text-slate-700">دفع آمن</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircle2" size={16} className="text-success" />
                    <span className="text-sm text-slate-700">ضمان الجودة</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default BookingWizard;
